'use client';

import * as React from 'react';
import { useSearchParams } from 'next/navigation';
import { MediaPickerItem } from '@/app/dashboard/cms/components/media-picker-item';
import { useAppSelector } from '@/store/hooks';
import { getUi, hideModal } from '@/store/reducers/ui-slice';
import { OperationResult, PaginatedResult } from '@alien-platform/bo-sdk';
import { Media } from '@alien-platform/cms-bo';
// import { useUser } from '@auth0/nextjs-auth0';
import { Button, CircularProgress, IconButton, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { Box } from '@mui/system';
import { ArrowsIn } from '@phosphor-icons/react/dist/ssr/ArrowsIn';
import { ArrowsOut } from '@phosphor-icons/react/dist/ssr/ArrowsOut';
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X';
import { useDispatch } from 'react-redux';

import { useMediaQuery } from '@/hooks/use-media-query';

import ItemsNotFound from './items-not-found';

export function MediaPickerModalContent(): React.JSX.Element {
  const { modal } = useAppSelector(getUi);

  const content = modal?.content as {
    target: {
      customKey: string;
      index: number;
      item: unknown;
    };
  };

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isFullScreen, setFullScreen] = React.useState(false);
  const searchParams = useSearchParams();
  const selectedOpId = searchParams.get('opId');
  const [items, setItems] = React.useState<Media[]>();
  const mdDown = useMediaQuery('down', 'md');

  const handleClose = (): void => {
    dispatch(hideModal());
  };
  const toggleFullScreen = (): void => {
    setFullScreen((prev) => !prev);
  };

  const getMediaItems = async (query?: string): Promise<void> => {
    try {
      setIsLoading(true);

      const res = await fetch(`/api/cms/gallery/list?opId=${selectedOpId}`, {
        method: 'POST',
        body: JSON.stringify({ tags: query }),
      });
      const result: OperationResult<PaginatedResult<Media>> = await res.json();
      if (res?.ok && result.success && result.data) {
        setItems(result.data.items);
      }
    } catch (e) {
      // empty block
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    void getMediaItems();
  }, []);

  return (
    <Stack>
      <Stack direction="row" sx={{ width: 1, minWidth: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Choose File</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          {mdDown ? null : (
            <IconButton onClick={toggleFullScreen}>{isFullScreen ? <ArrowsIn /> : <ArrowsOut />}</IconButton>
          )}

          <IconButton onClick={handleClose}>
            <XIcon />
          </IconButton>
        </Box>
      </Stack>
      <Stack sx={{ height: isFullScreen ? '90vh' : 'auto', overflow: 'auto', mb: !isFullScreen ? '100px' : '20px' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress color="primary" size={38} />
          </Box>
        ) : !items?.length ? (
          <ItemsNotFound />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: mdDown ? 2 : 4,
              gridTemplateColumns: isFullScreen
                ? {
                    xs: 'repeat(2, 1fr)',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(5, 1fr)',
                    xl: 'repeat(6, 1fr)',
                  }
                : {
                    xs: 'repeat(2, 1fr)',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(5, 1fr)',
                  },
              maxHeight: !isFullScreen ? '500px' : 'auto',
              p: 2,
            }}
          >
            {items?.map((item) => (
              <MediaPickerItem
                item={item}
                key={item._id}
                target={content?.target}
                onDelete={() => {
                  // empty block
                }}
                onFavorite={() => {
                  // empty block
                }}
                onOpen={() => {
                  // empty block
                }}
              />
            ))}
          </Box>
        )}
      </Stack>
      <Stack
        direction="row"
        spacing={3}
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: 1,
          mt: 3,
          justifyContent: 'initial',
          px: 12,
          py: 4,
          backgroundColor: 'var(--mui-palette-background-level1)',
        }}
      >
        <Button type="button" color="secondary" onClick={handleClose} sx={{ flex: '1 1 auto' }}>
          Close
        </Button>
        <Button loading={isLoading} color="primary" onClick={handleClose} sx={{ flex: '1 1 auto' }} variant="contained">
          Continue
        </Button>
      </Stack>
    </Stack>
  );
}
